<template>
  <div class="donation-background" :style="backgroundCSS">
    <Preview
      v-if="previewModal"
      :link="previewLink"
      @close="hidePreview"
      :label="labels.preview"
      :loading="previewLoading"
    />
    <div class="donation-wrapper">
      <div
        class="tribute-form__head"
        :style="headCSS"
        :class="{ 'donation-form__disable-hero': !background }"
      >
        <div
          :class="{ 'tribute-form__title-nobg': !background }"
          class="tribute-form__title"
        >
          <h1>{{ title }}</h1>
        </div>

        <div v-if="subtitle && subtitle !== ''" class="tribute-form__subtitle">
          <h2>{{ subtitle }}</h2>
        </div>

        <div class="tribute-form__body" v-html="body" />
      </div>

      <div class="tribute-form">
        <div
          v-if="pledgeTargets && pledgeTargets.length > 0"
          class="donation-form__fields donation-form__targets"
        >
          <div class="donation-form__label">
            <label>{{ labels.choosePledgeTarget }}</label>
          </div>
          <div class="donation-form__fields--field">
            <select
              v-model="form.pledgeTarget"
              @change="updatePledgeDetails"
              :class="{
                'tribute-form__form-control-valid':
                  validPledge && isValid('pledgeTarget'),
                'tribute-form__form-control-error':
                  validPledge && !isValid('pledgeTarget'),
              }"
            >
              <option
                v-for="target in pledgeTargets"
                :key="target.targetID"
                :value="target.kommed_id"
                :data-line1="target.line_1"
                :data-line2="target.line_2"
                :data-description="target.description"
              >
                {{ target.post_title }}
              </option>
            </select>
          </div>
        </div>

        <div class="donation-form__target-description">
          <p v-html="form.description"></p>
        </div>

        <div class="tribute-form__fields tribute-form__fields--cards">
          <div class="tribute-form__label">
            <label>{{ labels.cards_label }}</label>
          </div>
          <div
            v-for="card of cards"
            @change="preview"
            :key="card.name"
            class="tribute-form__fields--card"
          >
            <input
              v-model="form.card"
              type="radio"
              name="card"
              @click="setAllowPreview"
              :value="getCardObject(card)"
              :id="slugify(card.name)"
            />
            <label :for="slugify(card.name)">
              <img
                :src="
                  card.image.sizes.medium
                    ? card.image.sizes.medium
                    : card.image.url
                "
              />
            </label>
          </div>
        </div>

        <div class="tribute-form__fields tribute-form__fields--greeting">
          <div class="tribute-form__form-control">
            <label for="recipient">{{ labels.greeting_label }}</label>
            <div
              v-if="showDeceasedName"
              class="tribute-form__form-control tribute-form__fields--field"
            >
              <input
                v-model="form.deceased_name"
                :placeholder="labels.deceased_name"
                :class="{
                  'tribute-form__form-control-valid':
                    validDeceasedName && isValid('deceased_name'),
                  'tribute-form__form-control-error':
                    validDeceasedName && !isValid('deceased_name'),
                }"
                @change="checkValidation('deceased_name')"
              />
            </div>
            <vue-editor
              v-model="form.card_text"
              @input="debouncedPreview"
              @focus="emptyText"
              :editorbToolbar="[]"
              ref="vueEditor"
              class="tribute-form__form-control tribute-form__fields--field"
              :class="{
                'tribute-form__form-control-valid':
                  validate && isValid('card_text'),
                'tribute-form__form-control-error':
                  validate && !isValid('card_text'),
              }"
            />
          </div>
        </div>

        <div
          v-if="isMemorial"
          class="tribute-form__fields tribute-form__fields--datepicker"
        >
          <Datepicker
            v-model="form.burialDate"
            :placeholder="labels.burial_date"
            :language="language"
            format="yyyy-MM-dd"
            :disabledDates="disabledDates"
          />
        </div>

        <div class="preview-button">
          <button :disabled="!allowPreview" @click="showPreview">
            {{ labels.preview_button }}
          </button>
        </div>

        <div class="tribute-form__fields tribute-form__amounts">
          <div class="tribute-form__label">
            <label>{{ labels.amounts_label }}</label>
          </div>
          <template v-if="amounts.length">
            <div
              v-for="amount in amounts"
              :key="amount"
              @change="prefilledAmountChanged"
              class="tribute-form__form-control tribute-form__fields--field"
            >
              <input
                :id="makeId('amount-', amount)"
                v-model="prefilled"
                :value="amount"
                type="radio"
              />
              <label :for="makeId('amount-', amount)">{{
                amount | currency(showCurrency, currency)
              }}</label>
            </div>
          </template>
          <div
            @change="amountChanged"
            @click="amountChanged()"
            class="tribute-form__form-amount tribute-form__form-control tribute-form__fields--field"
          >
            <currency-input
              v-model="amount"
              :placeholder="labels.amount"
              :class="{
                'tribute-form__form-control-valid':
                  prefilled === null && isValid('amount'),
                'tribute-form__form-control-error':
                  prefilled === null && !isValid('amount'),
              }"
              :currency="currency"
              v-if="showCurrency"
            />
            <input
              v-model="amount"
              :placeholder="labels.amount"
              :class="{
                'tribute-form__form-control-valid':
                  prefilled === null && isValid('amount'),
                'tribute-form__form-control-error':
                  prefilled === null && !isValid('amount'),
              }"
              type="tel"
              v-else
            />
          </div>
        </div>

        <div class="tribute-form__fields tribute-form__fields--delivery">
          <div class="tribute-form__label">
            <label>{{ labels.delivery_label }}</label>
          </div>
          <div class="tribute-form__form-control tribute-form__fields--field">
            <select
              v-model="form.send_via"
              :class="{
                'tribute-form__form-control-valid':
                  validDelivery && isValid('send_via'),
                'tribute-form__form-control-error':
                  validDelivery && !isValid('send_via'),
              }"
            >
              <option value="null" disabled="disabled" selected>
                {{ labels.send_via }}
              </option>
              <option value="email" :disabled="emailDisabled">
                {{ labels.email }}
              </option>
              <option value="post">{{ labels.post }}</option>
            </select>
            <div v-if="extraShipping">
              {{ labels.extra_shipping }}: {{ shipping }} kr.
            </div>
          </div>
          <div class="tribute-form__form-control tribute-form__fields--field">
            <select
              v-if="form.send_via"
              v-model="form.send_to"
              :class="{
                'tribute-form__form-control-valid':
                  validDelivery && isValid('send_to'),
                'tribute-form__form-control-error':
                  validDelivery && !isValid('send_to'),
              }"
            >
              <option value="null" disabled="disabled" selected>
                {{ labels.send_to }}
              </option>
              <option value="me">{{ labels.to_me }}</option>
              <option v-if="!isMemorial" value="recipient">
                {{ labels.to_recipient }}
              </option>
              <option
                v-if="isMemorial && form.send_via !== 'email'"
                value="funeral"
              >
                {{ labels.funeral }}
              </option>
              <option v-if="isMemorial" value="relative">
                {{ labels.relative }}
              </option>
              <option
                v-if="isMemorial && sendToChurch && form.send_via !== 'email'"
                value="church"
              >
                {{ labels.church }}
              </option>
            </select>
          </div>

          <div v-if="form.send_to != null && form.send_to != 'me'">
            <div v-if="form.send_via == 'post'">
              <div class="tribute-form__fields tribute-form__fields--recipient">
                <div
                  v-if="form.send_to != 'funeral' && form.send_to != 'church'"
                  class="tribute-form__form-control tribute-form__fields--field"
                  :class="{
                    'tribute-form__form-control-valid-parent':
                      validRecipientName && isValid('recipient_firstName'),
                    'tribute-form__form-control-error-parent':
                      validRecipientName && !isValid('recipient_firstName'),
                  }"
                >
                  <input
                    v-model="form.recipient_firstName"
                    :placeholder="labels.recipient_firstName"
                    :class="{
                      'tribute-form__form-control-valid':
                        validRecipientName && isValid('recipient_firstName'),
                      'tribute-form__form-control-error':
                        validRecipientName && !isValid('recipient_firstName'),
                    }"
                    @change="checkValidation('recipient_firstName')"
                  />
                </div>
                <div
                  :class="{
                    'only-name':
                      form.send_to == 'funeral' || form.send_to == 'church',
                    'tribute-form__form-control-valid-parent':
                      validRecipientSurname && isValid('recipient_lastName'),
                    'tribute-form__form-control-error-parent':
                      validRecipientSurname && !isValid('recipient_lastName'),
                  }"
                  class="tribute-form__form-control tribute-form__fields--field"
                >
                  <input
                    v-model="form.recipient_lastName"
                    :placeholder="
                      form.send_to == 'funeral' || form.send_to == 'church'
                        ? labels.recipient_name
                        : labels.recipient_lastName
                    "
                    :class="{
                      'tribute-form__form-control-valid':
                        validRecipientSurname && isValid('recipient_lastName'),
                      'tribute-form__form-control-error':
                        validRecipientSurname && !isValid('recipient_lastName'),
                    }"
                    @change="checkValidation('recipient_lastName')"
                  />
                </div>
              </div>
              <div class="tribute-form__fields tribute-form__fields--recipient">
                <div
                  class="tribute-form__form-control tribute-form__fields--field"
                  :class="{
                    'tribute-form__form-control-valid-parent':
                      validRecipientAdress && isValid('recipient_address'),
                    'tribute-form__form-control-error-parent':
                      validRecipientAdress && !isValid('recipient_address'),
                  }"
                >
                  <input
                    v-model="form.recipient_address"
                    :placeholder="labels.recipient_address"
                    :class="{
                      'tribute-form__form-control-valid':
                        validRecipientAdress && isValid('recipient_address'),
                      'tribute-form__form-control-error':
                        validRecipientAdress && !isValid('recipient_address'),
                    }"
                    @change="checkValidation('recipient_address')"
                  />
                </div>
                <div
                  class="tribute-form__form-control tribute-form__fields--field"
                  :class="{
                    'tribute-form__form-control-valid-parent':
                      validRecipientZip && isValid('recipient_zip'),
                    'tribute-form__form-control-error-parent':
                      validRecipientZip && !isValid('recipient_zip'),
                  }"
                >
                  <input
                    v-model="form.recipient_zip"
                    :placeholder="labels.recipient_zip"
                    :class="{
                      'tribute-form__form-control-valid':
                        validRecipientZip && isValid('recipient_zip'),
                      'tribute-form__form-control-error':
                        validRecipientZip && !isValid('recipient_zip'),
                    }"
                    @change="checkValidation('recipient_zip')"
                  />
                </div>
              </div>
              <div class="tribute-form__fields tribute-form__fields--recipient">
                <div
                  class="tribute-form__form-control tribute-form__fields--field"
                  :class="{
                    'tribute-form__form-control-valid-parent':
                      validRecipientCity && isValid('recipient_city'),
                    'tribute-form__form-control-error-parent':
                      validRecipientCity && !isValid('recipient_city'),
                  }"
                >
                  <input
                    v-model="form.recipient_city"
                    :placeholder="labels.recipient_city"
                    :class="{
                      'tribute-form__form-control-valid':
                        validRecipientCity && isValid('recipient_city'),
                      'tribute-form__form-control-error':
                        validRecipientCity && !isValid('recipient_city'),
                    }"
                    @change="checkValidation('recipient_city')"
                  />
                </div>
              </div>
            </div>
            <div
              v-else
              class="tribute-form__form-control tribute-form__fields--recipient"
            >
              <input
                v-model="form.recipient_email"
                :placeholder="labels.recipient_contact"
                :class="{
                  'tribute-form__form-control-valid':
                    validRecipientEmail && isValid('recipient_email'),
                  'tribute-form__form-control-error':
                    validRecipientEmail && !isValid('recipient_email'),
                }"
                @change="checkValidation('recipient_email')"
              />
            </div>
          </div>
        </div>

        <div class="tribute-form__fields tribute-form__fields--continue">
          <div class="tribute-form__form-control">
            <button @click.prevent="stepPassed(1)">{{ labels.submit }}</button>
          </div>
        </div>

        <div v-if="gdpr !== ''" v-html="gdpr" class="tribute-form__gdpr" />

        <div
          id="contact"
          v-if="step > 1"
          class="tribute-form__fields tribute-form__mailandtel"
        >
          <div class="tribute-form__label">
            <label>{{ labels.contact }}</label>
          </div>
          <div
            class="tribute-form__form-control tribute-form__fields--field"
            :class="{
              'tribute-form__form-control-valid-parent':
                validEmail && isValid('email'),
              'tribute-form__form-control-error-parent':
                validEmail && !isValid('email'),
            }"
          >
            <input
              v-model="form.email"
              :placeholder="labels.email"
              :class="{
                'tribute-form__form-control-valid':
                  validEmail && isValid('email'),
                'tribute-form__form-control-error':
                  validEmail && !isValid('email'),
              }"
              @change="checkValidation('email')"
              type="email"
              name="email"
            />
          </div>
          <div
            class="tribute-form__form-control tribute-form__fields--field"
            :class="{
              'tribute-form__form-control-valid-parent':
                validPhone && isValid('phone'),
              'tribute-form__form-control-error-parent':
                validPhone && !isValid('phone'),
            }"
          >
            <input
              v-model="form.phone"
              :placeholder="labels.phone"
              :class="{
                'tribute-form__form-control-valid':
                  validPhone && isValid('phone'),
                'tribute-form__form-control-error':
                  validPhone && !isValid('phone'),
              }"
              @change="checkValidation('phone')"
              type="tel"
              name="phone"
            />
          </div>
        </div>

        <div
          v-if="step > 1 && paymentMethods.length > 1"
          class="tribute-form__fields tribute-form__methods"
        >
          <div class="tribute-form__label">
            <label>{{ labels.payment_label }}</label>
          </div>
          <div
            v-for="method in paymentMethods"
            :key="method"
            :class="{ 'tribute-form__fields-pristine': form.method === null }"
            class="tribute-form__fields--field"
          >
            <input
              :id="makeId('method-', method)"
              v-model="form.method"
              type="radio"
              :value="method"
            />
            <label
              :for="makeId('method-', method)"
              :class="makeClass(method)"
              >{{ labels[method] }}</label
            >
          </div>
        </div>

        <div id="errors" v-if="errorPositionTop === true">
          <div class="tribute-form__error" v-html="error" />
          <div class="tribute-form__success" v-html="message" />
        </div>

        <div v-if="step > 1" class="tribute-form__address-wrapper">
          <div class="tribute-form__label">
            <label>{{ labels.contact }}</label>
          </div>
          <div
            v-if="step > 1 && getAddressFromSocialSecurity === true"
            class="tribute-form__fields tribute-form__getaddress"
          >
            <div class="tribute-form__form-control tribute-form__fields--field">
              <input
                v-model="form.socialSecurityNumber"
                :placeholder="labels.socialSecurityNumber"
                type="tel"
                name="socialsecuritynumber"
              />
            </div>
            <div class="tribute-form__form-control tribute-form__fields--field">
              <button
                @click.prevent="getOrEmptyAddress()"
                :class="{
                  'tribute-form__button-loading':
                    addressRetrievalInProgress === true,
                }"
                id="bank-button"
              >
                {{ addressLabel }}
              </button>
            </div>
          </div>

          <div v-if="step > 1" class="tribute-form__fields tribute-form__names">
            <div class="tribute-form__form-control tribute-form__fields--field">
              <maskable-input
                :masked="hideAddress && maskAddress"
                v-model="form.firstName"
                :placeholder="labels.firstName"
                :class="{
                  'tribute-form__form-control-valid':
                    validName && isValid('firstName'),
                  'tribute-form__form-control-error':
                    validName && !isValid('firstName'),
                }"
                name="firstname"
              />
            </div>
            <div class="tribute-form__form-control tribute-form__fields--field">
              <maskable-input
                :masked="hideAddress && maskAddress"
                v-model="form.lastName"
                :placeholder="labels.lastName"
                :class="{
                  'tribute-form__form-control-valid':
                    validSurname && isValid('lastName'),
                  'tribute-form__form-control-error':
                    validSurname && !isValid('lastName'),
                }"
                name="lastname"
              />
            </div>
          </div>
          <div
            v-if="isCompany && step > 1"
            class="tribute-form__fields tribute-form__company"
          >
            <div class="tribute-form__form-control tribute-form__fields--field">
              <input
                v-model="form.company"
                :placeholder="labels.company"
                name="company"
              />
            </div>
            <div class="tribute-form__form-control tribute-form__fields--field">
              <input
                v-model="form.department"
                :placeholder="labels.department"
                name="department"
              />
            </div>
          </div>
          <div
            v-if="step > 1"
            class="tribute-form__fields tribute-form__address"
          >
            <div class="tribute-form__form-control tribute-form__fields--field">
              <maskable-input
                :masked="hideAddress && maskAddress"
                v-model="form.address"
                :placeholder="labels.address"
                :class="{
                  'tribute-form__form-control-valid':
                    validAdress && isValid('address'),
                  'tribute-form__form-control-error':
                    validAdress && !isValid('address'),
                }"
                name="address"
              />
            </div>
            <div class="tribute-form__form-control tribute-form__fields--field">
              <maskable-input
                v-model="form.address2"
                :masked="hideAddress && maskAddress"
                :placeholder="labels.address2"
                name="co"
              />
            </div>
          </div>
          <div v-if="step > 1" class="tribute-form__fields tribute-form__city">
            <div class="tribute-form__form-control tribute-form__fields--field">
              <maskable-input
                :masked="hideAddress && maskAddress"
                v-model="form.zip"
                :placeholder="labels.zip"
                :class="{
                  'tribute-form__form-control-valid':
                    validZip && isValid('zip'),
                  'tribute-form__form-control-error':
                    validZip && !isValid('zip'),
                }"
                name="zip"
              />
            </div>
            <div class="tribute-form__form-control tribute-form__fields--field">
              <maskable-input
                :masked="hideAddress && maskAddress"
                v-model="form.city"
                :placeholder="labels.city"
                :class="{
                  'tribute-form__form-control-valid':
                    validCity && isValid('city'),
                  'tribute-form__form-control-error':
                    validCity && !isValid('city'),
                }"
                name="city"
              />
            </div>
          </div>

          <div v-if="step > 1 && terms !== ''" class="tribute-form__terms">
            <div
              v-html="showTerms ? terms : $options.filters.preview(terms, 75)"
              class="tribute-form__terms-preview"
            />
            <div
              v-if="$options.filters.stripTags(terms).length > 75"
              class="tribute-form__terms-toggle"
            >
              <a @click.prevent="toggleTerms" href="#">{{
                showTerms ? labels.showLess : labels.showMore
              }}</a>
            </div>
          </div>
        </div>

        <div id="errors" v-if="errorPositionTop === false">
          <div class="tribute-form__error" v-html="error" />
          <div class="tribute-form__success" v-html="message" />
        </div>

        <div
          v-if="step > 1"
          class="tribute-form__form-control tribute-form__submit"
        >
          <button
            @click.prevent="submit"
            :disabled="paymentButtonLoading || submitDisabled"
            :class="{
              'tribute-form__button-loading': paymentButtonLoading === true,
            }"
          >
            {{ labels.submit }}
          </button>
        </div>

        <div
          id="stripe-wrapper"
          v-if="hasStripe"
          v-show="showStripe"
          class="tribute-form__stripe"
        >
          <Stripe
            @completed="finishStripe"
            @initiated="initiateStripe"
            :pk="stripeKey"
            :labels="labels"
            :amount="form.amount"
            :name="fullName"
          />
        </div>

        <Swish
          v-if="hasSwish"
          v-show="showSwish"
          @close="closeSwish"
          class="tribute-form__swish"
          :lead-id="form.leadId"
          :amount="form.amount"
          :labels="labels"
          :name="fullName"
          :phone="form.phone"
          :payment-id="paymentId"
        />
      </div>
    </div>
  </div>
</template>

<script>
import 'idempotent-babel-polyfill';

import CurrencyInput from './CurrencyInput.vue';
import Preview from './Preview';
import { VueEditor } from 'vue2-editor';
import donationMix from './mixin.js';
import Datepicker from 'vuejs-datepicker';
import MaskableInput from './MaskableInput';
import { en, sv } from 'vuejs-datepicker/dist/locale';
import Stripe from './Stripe.vue';
import Swish from './Swish.vue';

export default {
  mixins: [donationMix],

  name: 'Tribute',

  components: {
    CurrencyInput,
    Datepicker,
    VueEditor,
    MaskableInput,
    Stripe,
    Swish,
    Preview,
  },

  props: {
    paymentMethods: {
      type: Array,
      default: () => [],
    },
    cards: {
      type: Array,
      default: () => [],
    },
    terms: {
      type: String,
      default: null,
    },
    confirmation: {
      type: String,
      default: null,
    },
    template: {
      type: String,
      default: 'tribute',
    },
    locale: {
      type: String,
      default: 'sv_SE',
    },
    showDeceasedName: {
      type: Boolean,
      default: false,
    },
    shipping: {
      type: Number,
      default: null,
    },
    clearText: {
      type: Boolean,
      default: false,
    },
    sendToChurch: {
      type: Boolean,
      default: false,
    },
    productType: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      previewModal: false,
      validate: false,
      validEmail: false,
      validPledge: false,
      validDelivery: false,
      validPhone: false,
      validSsn: false,
      validName: false,
      validSurname: false,
      validAdress: false,
      validZip: false,
      validCity: false,
      validRecipientEmail: false,
      validRecipientName: false,
      validRecipientSurname: false,
      validDeceasedName: false,
      validRecipientAdress: false,
      validRecipientZip: false,
      validRecipientCity: false,
      scrolled: false,
      previewLoading: false,
      previewTimeout: null,
      touched: false,
      placeholderResetted: false,
      en: en,
      sv: sv,
      allowPreview: false,
      form: {
        recipient_email: null,
        recipient_firstName: null,
        recipient_lastName: null,
        recipient_address: null,
        recipient_zip: null,
        recipient_city: null,
        card: null,
        send_via: null,
        send_to: null,
        card_text: null,
        burialDate: null,
        template: null,
        deceased_name: null,
      },
      replacementText: null,
    };
  },

  watch: {
    showStripe: function (value) {
      if (value) {
        setTimeout(() =>
          // eslint-disable-next-line prettier/prettier
          this.$scrollTo('#stripe-wrapper', 500, { offset: -370 }),
        );
      }
    },
    'form.method': function () {
      this.submitDisabled = false;
    },
  },

  mounted() {
    this.step = 1;
    this.form.template = this.template;
    this.form.card = this.getCardObject(this.cards[0]);
    this.form.card_text = this.isMemorial
      ? this.labels.burial_placeholder
      : this.labels.greeting_placeholder;
    this.formName = this.title;
    if (this.leadId !== null) {
      if (this.form.method === null) {
        this.form.method = this.paymentMethods[0];
      }
      this.step = 2;
    }
    this.form.amount = this.prefilled ? this.prefilled : parseInt(this.amount);
    if (this.pledgeTargets.length && !this.form.pledgeTarget) {
      setTimeout(() => {
        this.form.pledgeTarget = this.pledgeTargets[0].kommed_id;
        this.form.line1 = this.pledgeTargets[0].line_1;
        this.form.line2 = this.pledgeTargets[0].line_2;
        this.form.card.line1 = this.pledgeTargets[0].line_1;
        this.form.card.line2 = this.pledgeTargets[0].line_2;
        this.form.description = this.pledgeTargets[0].description;
      }, 500);
    }
    if (this.cards.length > 0) {
      this.form.card = this.getCardObject(this.cards[0]);
      this.$nextTick(() => {
        document.getElementById(
          this.slugify(this.cards[0].name),
        ).checked = true;
      });
    }
    this.preview();
  },

  computed: {
    extraShipping() {
      return (
        this.form.send_via &&
        this.form.send_via === 'post' &&
        this.shipping &&
        this.shipping > 0
      );
    },

    emailDisabled() {
      return this.form.send_to === 'funeral' || this.form.send_to === 'church';
    },

    isMemorial() {
      return this.template === 'memorial';
    },

    validationRegExpr() {
      return { ...this.defaultValidation, ...this.validation };
    },

    backgroundCSS() {
      return this.background && this.imagePosition == 0
        ? "background-image: url('" + this.background + "')"
        : null;
    },

    headCSS() {
      return this.background && this.imagePosition == 1
        ? "background-image: url('" + this.background + "')"
        : null;
    },

    returnUrl() {
      return this.confirmation;
    },

    paymentButtonLoading() {
      return this.payexRedirectInProgress;
    },

    customerData() {
      return {
        text: this.form.card_text,
      };
    },

    language() {
      return this.locale === 'sv_SE' ? this.se : this.en;
    },

    disabledDates() {
      return {
        to: new Date(),
      };
    },

    hasStripe() {
      return this.paymentMethods.indexOf('stripe') > -1;
    },

    hasSwish() {
      return this.paymentMethods.indexOf('swish-direct') > -1;
    },

    selectedTarget() {
      const t =
        this.pledgeTargets && this.form.pledgeTarget
          ? Object.values(this.pledgeTargets).find(
              (p) => p.targetID === this.form.pledgeTarget,
            )
          : null;
      return t ? t.targetDescription : null;
    },
  },

  methods: {
    updatePledgeDetails(event) {
      const selectedOption = event.target.selectedOptions[0];
      this.form.line1 = selectedOption.dataset.line1;
      this.form.line2 = selectedOption.dataset.line2;
      this.form.description = selectedOption.dataset.description;
    },

    showPreview() {
      this.previewModal = true;
    },

    hidePreview() {
      this.previewModal = false;
    },

    stepPassed(step) {
      this.error = null;
      this.validate = true;
      this.validDelivery = true;

      switch (step) {
        case 1:
          if (this.pledgeTargets) {
            this.validPledge = true;
          }
          if (this.showDeceasedName) {
            this.validDeceasedName = true;
          }

          if (!this.validateRecipient()) {
            break;
          }

          if (this.validPledge && !this.isValid('pledgeTarget')) {
            this.error = this.labels.errorPledge;
          } else if (this.validDeceasedName && !this.isValid('deceased_name')) {
            this.error = this.labels.errorDeceased;
          } else if (this.prefilled === null && !this.isValid('amount')) {
            this.error = this.labels.errorNoAmount;
          } else if (!this.areValid(['recipient', 'card_text'])) {
            this.error = this.labels.errorRequiredFields;
          } else if (!this.isValid('card')) {
            this.error = this.labels.errorMissingCard;
          } else if (
            this.form.send_via === null ||
            this.form.send_to === null
          ) {
            this.error = this.labels.errorSendingMethod;
          } else if (
            this.template === 'memorial' &&
            !this.isValid(['burialDate'])
          ) {
            this.error = this.labels.errorMissingBurialDate;
          } else {
            this.validate = false;
            this.step = 2;
            this.$scrollTo('#contact', 500, { offset: -100 });
            this.pushDataLayerStep(1);
          }
          break;
      }
    },

    emptyText() {
      if (!this.placeholderResetted && this.clearText) {
        this.form.card_text = '';
        this.placeholderResetted = true;
        this.$refs.vueEditor.quill.root.innerHTML = '';
      }
    },

    validateRecipient() {
      if (
        this.form.send_via === 'post' &&
        this.form.send_to !== 'me' &&
        this.form.send_to !== 'realtive'
      ) {
        if (
          !this.validRecipientSurname ||
          !this.isValid('recipient_lastName') ||
          !this.validRecipientAdress ||
          !this.isValid('recipient_address') ||
          !this.validRecipientZip ||
          !this.isValid('recipient_zip') ||
          !this.validRecipientCity ||
          !this.isValid('recipient_city')
        ) {
          this.error = 'Fyll i mottagarens uppgifter';
          return false;
        }
      }
      if (
        this.form.send_to == 'relative' &&
        (!this.validRecipientName || !this.isValid('recipient_firstName')) &&
        this.form.send_via === 'post'
      ) {
        this.error = 'Fyll i mottagarens förnamn';
        return false;
      }

      if (
        this.form.send_to == 'relative' &&
        (!this.validRecipientEmail || !this.isValid('recipient_email')) &&
        this.form.send_via === 'email'
      ) {
        this.error = 'Fyll i mottagarens e-post';
        return false;
      }
      return true;
    },

    submit() {
      this.validate = true;
      this.validEmail = true;
      this.validPhone = true;
      this.validSsn = true;
      this.validName = true;
      this.validSurname = true;
      this.validAdress = true;
      this.validZip = true;
      this.validCity = true;
      this.validDelivery = true;
      if (this.form.send_to === 'recipient') {
        this.validRecipientEmail = true;
      }

      if (!this.validateRecipient()) {
        return false;
      }

      if (this.form.send_via === 'post' && this.form.send_to === 'recipient') {
        this.validRecipientName = true;
        this.validRecipientSurname = true;
        this.validRecipientAdress = true;
        this.validRecipientZip = true;
        this.validRecipientCity = true;
      }
      this.form.amount = this.prefilled
        ? this.prefilled
        : parseInt(this.amount);
      if (this.form.amount === null || isNaN(this.form.amount)) {
        this.error = this.labels.errorNoAmount;
        return false;
      }
      if (this.form.amount < this.min) {
        this.error = this.labels.errorMinimum.replace(/_AMOUNT_/, this.min);
        return false;
      }
      if (this.form.method === null && this.paymentMethods.length === 1) {
        this.form.method = this.paymentMethods[0];
      }
      if (this.form.method === null) {
        this.error = this.labels.errorNoPaymentMethod;
        return false;
      } else if (
        this.form.method === 'swish' ||
        this.form.method === 'creditcard' ||
        this.form.method === 'directdebit'
      ) {
        this.message = this.labels.redirectPayex;
        this.pushDataLayer();
        return this.payex();
      } else if (this.form.method === 'stripe') {
        this.submitDisabled = true;
        this.showStripe = true;
        this.pushDataLayer();
        return this.saveLead();
      } else if (this.form.method === 'swish-direct') {
        this.pushDataLayer();
        return this.swish();
      } else {
        this.error = this.labels.error;
      }
    },

    pushDataLayer() {
      window.dataLayer.push({
        event: 'purchase',
        product: this.productType,
        value: this.form.amount,
        currency: 'SEK',
        payment_method: this.form.method,
      });
    },

    pushDataLayerStep(step) {
      window.dataLayer.push({
        event: 'module-step-viewed',
        product: this.productType,
        step_name: step,
      });
    },

    prefilledAmountChanged() {
      this.form.amount = this.prefilled;
      this.amount = null;
    },

    amountChanged() {
      this.form.amount = parseInt(this.amount);
      this.prefilled = null;
    },

    makeId(base, id) {
      return base + id;
    },

    makeClass(method) {
      return 'donation-form__method-' + method;
    },

    debouncedPreview() {
      if (this.touched === false) {
        this.touched = true;
        return false;
      }
      let textWithoutMarkup = document
        .querySelector('.ql-editor')
        .innerHTML.replace(/(<([^>]+)>)/gi, '');

      if (textWithoutMarkup.length < 863) {
        this.replacementText = textWithoutMarkup.substring(0, 863);
        clearTimeout(this.previewTimeout);
        let that = this;
        this.previewTimeout = setTimeout(function () {
          that.preview();
        }, 500);
      } else if (this.form.card_text.length > 862) {
        document.querySelector('.ql-editor').innerHTML = this.replacementText;
      }
    },

    setAllowPreview() {
      this.allowPreview = true;
    },

    preview() {
      if (
        this.previewLoading === false &&
        this.areValid(['card', 'card_text'])
      ) {
        this.previewLoading = true;
        this.getPreviewLink(this.form.card, this.customerData);
      }
    },

    getCardObject(card) {
      return card !== null
        ? {
            x: card.card_x,
            y: card.card_y,
            id: card.pdf.ID,
            card_width: card.card_width,
            negative: card.negative,
            target: this.selectedTarget,
            name: card.name,
            url: card.image.sizes.medium,
            first_line: this.form.line1,
            second_line: this.form.line2,
          }
        : null;
    },
  },
};
</script>

<style lang="scss">
.tribute-form {
  text-align: center;
  margin: auto;
  max-width: 800px;

  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &__fields {
    display: flex;
    justify-content: center;
    margin: 20px;
    &--field {
      flex: 0 0 auto;
      input[type='radio'] {
        position: absolute;
        left: -9999px;
      }
      label {
        padding: 10px;
        cursor: pointer;
        color: black;
      }
      input[type='radio']:checked + label {
        background: green;
        color: white;
      }
    }
    &--greeting {
      width: 100%;
    }
    &--name,
    &--recipient {
      width: 100%;
      input {
        margin-left: auto;
        margin-right: auto;
        width: 80%;
      }
    }
    &--card {
      flex: 0 0 auto;
      margin: 5px;
      img {
        max-width: 300px;
      }

      input[type='radio'] {
        position: absolute;
        left: -9999px;
      }
      input[type='radio']:checked + label {
        img {
          box-shadow: 0 0 0 2px rgba(9, 210, 100, 1);
        }
      }
    }
  }

  &__error {
    color: red;
  }

  &__success {
    color: green;
  }

  &__form-control {
    margin: 10px;

    input,
    button,
    select {
      padding: 10px;
      width: 300px;
    }

    &-valid {
      border: 1px solid green;
    }

    &-error {
      border: 1px solid red;
    }
  }

  &__terms-toggle {
    font-size: 75%;
    text-decoration: underline;
  }

  .ql-editor {
    p {
      text-align: center !important;
    }
  }

  .ql-toolbar {
    display: none !important;
  }

  .ql-container {
    border-top: 1px solid #ccc !important;
  }
}
</style>
